import { useEffect, useState } from "react";
import { MAX_LABEL_LENGTH, type Pin } from "@tarkov-callout/shared";
import { t } from "../i18n";
import { pinKindColor, pinKindGlyph } from "../map/pinIcon";

interface Props {
  pin: Pin;
  isMine: boolean;
  mapName: string;
  onLabel: (label: string) => void;
  onRemove: () => void;
  onClose: () => void;
}

const COMPASS = ["n", "ne", "e", "se", "s", "sw", "w", "nw"] as const;

/** Describes a heading in the map's own frame, which is what everyone sees. */
function describeHeading(heading: number | null): string {
  if (heading === null) return t("pin.noFacing");
  const point = COMPASS[Math.round(heading / 45) % 8] ?? "n";
  return t("pin.facingValue", {
    compass: t(`compass.${point}`),
    degrees: Math.round(heading),
  });
}

export function PinCard({ pin, isMine, mapName, onLabel, onRemove, onClose }: Props) {
  const [draft, setDraft] = useState(pin.label);
  /** A stroke is a shape, not a callout: there is nothing to name or to face. */
  const isStroke = pin.kind === "draw";

  // Reset when a different pin is selected, or when someone else renames this one.
  useEffect(() => {
    setDraft(pin.label);
  }, [pin.id, pin.label]);

  const commit = () => {
    if (draft !== pin.label) onLabel(draft);
  };

  return (
    <div className="pin-card" style={{ borderColor: pin.color }}>
      <header className="pin-card__head">
        {/* Badge follows the marker's fill, the card's edge follows its ring. */}
        <span
          className="pin-card__kind"
          style={{ background: pinKindColor(pin.kind, pin.color) }}
        >
          <span aria-hidden="true">{pinKindGlyph(pin.kind)}</span>
          {t(`kind.${pin.kind}`)}
        </span>
        <span className="pin-card__author">{pin.authorName}</span>
        <button type="button" className="pin-card__close" onClick={onClose} aria-label={t("pin.close")}>
          ✕
        </button>
      </header>

      {!isStroke && (
      <input
        className="pin-card__input"
        value={draft}
        maxLength={MAX_LABEL_LENGTH}
        placeholder={t("pin.notePlaceholder")}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            commit();
            event.currentTarget.blur();
          }
        }}
      />
      )}

      <dl className="pin-card__meta">
        {!isStroke && (
        <div>
          <dt>{t("pin.facing")}</dt>
          <dd>{describeHeading(pin.heading)}</dd>
        </div>
        )}
        <div>
          <dt>{t("pin.floor")}</dt>
          <dd>{pin.layer ?? t("sidebar.groundFloor")}</dd>
        </div>
        <div>
          <dt>{t("pin.map")}</dt>
          <dd>{mapName}</dd>
        </div>
      </dl>

      <button type="button" className="pin-card__remove" onClick={onRemove}>
        {isMine ? t("pin.removeMine") : t("pin.removeOther")}
      </button>
    </div>
  );
}
